'use client';

import { useState } from 'react';
import { Send, User, Mail, MessageSquare, Lock } from 'lucide-react';

interface CondolenceFormProps {
  memorialSlug: string;
}

export function CondolenceForm({ memorialSlug }: CondolenceFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !message.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/memorials/${memorialSlug}/condolences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim() || null,
          descriptions: message.trim(),
          isPublic,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit condolence');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-12 bg-memorial-muted/30">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-xl p-8 shadow-sm border border-memorial-muted text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-serif text-xl text-memorial-primary mb-2">
              Vielen Dank für Ihre Anteilnahme
            </h3>
            <p className="text-memorial-text-secondary">
              Ihre Kondolenz wurde übermittelt und wird nach Prüfung veröffentlicht.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-memorial-muted/30">
      <div className="max-w-2xl mx-auto px-4">
        <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary text-center mb-2">
          Kondolenz schreiben
        </h2>
        <p className="text-center text-memorial-text-secondary mb-8">
          Teilen Sie Ihre Anteilnahme mit der Familie
        </p>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-memorial-muted">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* First name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-memorial-text-primary mb-1">
                Vorname *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary/50" />
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Ihr Vorname"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-memorial-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/50 focus:border-memorial-primary"
                />
              </div>
            </div>

            {/* Last name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-memorial-text-primary mb-1">
                Nachname *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary/50" />
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Ihr Nachname"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-memorial-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/50 focus:border-memorial-primary"
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-memorial-text-primary mb-1">
              E-Mail (optional, für Rückfragen)
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary/50" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre.email@beispiel.de"
                className="w-full pl-10 pr-4 py-2.5 border border-memorial-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/50 focus:border-memorial-primary"
              />
            </div>
          </div>

          {/* Message */}
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-memorial-text-primary mb-1">
              Ihre Nachricht *
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-memorial-text-secondary/50" />
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Schreiben Sie hier Ihre Worte des Beileids..."
                required
                rows={5}
                className="w-full pl-10 pr-4 py-2.5 border border-memorial-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/50 focus:border-memorial-primary resize-none"
              />
            </div>
          </div>

          {/* Privacy option */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!isPublic}
                onChange={(e) => setIsPublic(!e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-memorial-muted text-memorial-primary focus:ring-memorial-primary/50"
              />
              <div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-memorial-text-secondary" />
                  <span className="text-sm font-medium text-memorial-text-primary">
                    Nachricht nur für die Familie sichtbar
                  </span>
                </div>
                <p className="text-xs text-memorial-text-secondary mt-0.5">
                  Ihre Kondolenz wird nicht öffentlich angezeigt
                </p>
              </div>
            </label>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting || !firstName.trim() || !lastName.trim() || !message.trim()}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Kondolenz senden</span>
              </>
            )}
          </button>

          <p className="text-xs text-memorial-text-secondary text-center mt-4">
            Ihre Nachricht wird vor Veröffentlichung geprüft.
          </p>
        </form>
      </div>
    </section>
  );
}
